import { Player } from '@prisma/client';
import type { ActionFunctionArgs, LoaderFunctionArgs, MetaFunction } from 'react-router';
import { Link, useFetcher, useLoaderData } from 'react-router';
import shuffle from 'lodash/shuffle';
import { useEffect, useRef } from 'react';
import ConfirmButtonForm from '~/components/ConfirmButtonForm';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/forms/Select';
import { Icon } from '~/components/Icon';
import RoleEmoji from '~/components/RoleEmoji';
import { prisma } from '~/utils/db.server';
import DraftPlayerItem from './DraftPlayerItem';

export const meta: MetaFunction = () => {
  return [
    { title: 'Play KQ' },
    { name: 'description', content: 'Killer Queen Arcade Info & Tools' },
  ];
};
export const loader = async ({ params }: LoaderFunctionArgs) => {
  const playSessionSlug = params.playSessionSlug;
  const playSession = await prisma.playSession.findUnique({
    where: {
      slug: playSessionSlug,
    },
    select: {
      name: true,
      id: true,
      slug: true,
      players: {
        select: {
          id: true,
          name: true,
          role: {
            select: {
              name: true,
            },
          },
          teamId: true,
          teamAsQueen: true,
        },
      },
      teams: {
        select: {
          id: true,
          name: true,
          order: true,
          players: {
            select: {
              id: true,
              name: true,
              role: {
                select: {
                  name: true,
                },
              },
            },
          },
        },
      },
    },
  });
  const roles = await prisma.role.findMany();
  return {
    ...playSession,
    roles,
  };
};

export const action = async ({ request }: ActionFunctionArgs) => {
  const formData = await request.formData();
  const intent = formData.get('intent');
  const playSessionId = formData.get('playSessionId');
  switch (intent) {
    case 'add-player': {
      const playerName = formData.get('playerName');
      const roleId = formData.get('roleId');
      try {
        const newPlayer = await prisma.player.create({
          data: {
            name: playerName as string,
            role: {
              connect: {
                id: Number(roleId) ?? 0,
              },
            },
            playSession: {
              connect: {
                id: playSessionId as string,
              },
            },
          },
        });
        if (newPlayer) {
          console.log('Player Created', newPlayer);
          return {};
        } else {
          throw new Error('Failed to create player');
        }
      } catch (error) {
        console.error(error);
      }
      break;
    }
    case 'place-player': {
      const playerId = formData.get('playerId');
      const teamId = formData.get('teamId');
      console.log({ playerId, teamId });
      if (!playerId) {
        // throw new Error('No player or team provided');
        console.error('No player or team provided');
        return null;
      }
      await prisma.player.update({
        where: {
          id: playerId as string,
        },
        data: {
          team: teamId
            ? {
                connect: {
                  id: teamId as string,
                },
              }
            : { disconnect: true },
        },
      });
      break;
    }
    case 'remove-player': {
      const playerId = formData.get('playerId');
      await prisma.player.delete({
        where: {
          id: playerId as string,
        },
      });
      break;
    }
    case 'remove-team': {
      const teamId = formData.get('teamId');
      await prisma.team.delete({
        where: {
          id: teamId as string,
        },
      });
      break;
    }
    case 'generate-teams': {
      const queenIds = shuffle(
        JSON.parse(formData.get('queens')?.toString() ?? '')
      );
      if (!queenIds) {
        throw new Error('No queens provided');
      }
      try {
        const updated = await prisma.playSession.update({
          where: {
            id: playSessionId as string,
          },
          select: { id: true },
          data: {
            teams: {
              upsert: queenIds.map((queen: Player, index) => {
                return {
                  where: {
                    teamQueen: {
                      playSessionId: playSessionId as string,
                      queenId: queen.id,
                    },
                  },
                  create: {
                    name: `${queen.name}`,
                    order: index,
                    queen: {
                      connect: {
                        id: queen.id,
                      },
                    },
                  },
                  update: {
                    order: index,
                  },
                };
              }),
            },
          },
        });
        if (!updated) {
          throw new Error('Failed to generate teams', updated);
        }
      } catch (error) {
        console.error(error);
        throw new Error(`Failed At Query Level`);
      }
      break;
    }
    case 'reset-teams': {
      console.log('resetting teams');
      await prisma.player.updateMany({
        where: {
          playSessionId: playSessionId as string,
          roleId: {
            not: 1,
          },
        },
        data: {
          teamId: null,
        },
      });
      break;
    }
    case 'delete-teams': {
      await prisma.team.deleteMany({
        where: {
          playSessionId: playSessionId as string,
        },
      });
      break;
    }
  }
  return true;
};

export default function Index() {
  const playSession = useLoaderData<typeof loader>();
  const fetcher = useFetcher<typeof action>();
  const playerForm = useRef<HTMLFormElement>(null);
  const playerInput = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (playerForm.current && playerInput.current) {
      // playerForm.current.reset();
      playerInput.current.blur();
      playerInput.current.value = '';

      playerInput.current.focus();
    }
  }, [fetcher.state]);
  return (
    <div className="size-full min-h-svh ">
      <header className="text-sm tracking-wide px-4 py-2 border-b border-b-slate-800 text-slate-300 uppercase flex items-center gap-8 justify-between">
        <h1>{playSession?.name}</h1>
        <Link to="/draft" className="text-teal-400 hover:text-teal-600">
          <Icon name="close" size="md" />
        </Link>
      </header>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 p-6">
        <div className="flex flex-col gap-6">
          <fetcher.Form
            method="post"
            className="flex gap-0 "
            action={`/draft/${playSession.slug}?index`}
            ref={playerForm}
          >
            <input type="hidden" name="playSessionId" value={playSession?.id} />
            <Select name="roleId" defaultValue="1">
              <SelectTrigger
                className="text-lg rounded-r-none w-12"
                hideCaret={true}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {playSession?.roles.map((role) => (
                  <SelectItem
                    key={role.id}
                    value={role.id.toString()}
                    className="text-lg"
                  >
                    <RoleEmoji rolename={role.name} />
                    <span className="ml-1">{role.name}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input
              ref={playerInput}
              type="text"
              name="playerName"
              className="flex-grow rounded-none"
              required
            />

            <button
              type="submit"
              name="intent"
              value="add-player"
              className="btn font-medium flex items-center !rounded-l-none"
            >
              <Icon name="add" /> Add
            </button>
          </fetcher.Form>
          <h2 className="text-lg">
            Players{' '}
            {playSession?.players?.length && (
              <span className="ml-2 text-sm p-0.5 px-1 rounded bg-slate-700">
                {playSession?.players?.length}
              </span>
            )}
          </h2>
          <ul className="flex flex-col gap-2">
            {playSession?.players
              ?.filter(
                (player) => !player?.teamId && !player.teamAsQueen?.length
              )
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((player) => (
                <DraftPlayerItem key={player.id} player={player} />
              ))}
          </ul>
        </div>
        <div className="flex flex-col gap-6">
          <h2 className="text-lg">
            Teams{' '}
            {playSession?.teams?.length && (
              <span className="ml-2 text-sm p-0.5 px-1 rounded bg-slate-700">
                {playSession?.teams?.length}
              </span>
            )}
          </h2>

          <ul className="flex flex-col gap-2">
            {playSession?.teams?.map((team) => (
              <li
                key={team.id}
                className="flex flex-col gap-2 bg-black rounded border border-slate-800"
              >
                <div className="flex items-center justify-between bg-slate-900 border-b-slate-800 border-b p-2 px-3 ">
                  <h3>
                    <RoleEmoji rolename="queen" /> {team.name}
                  </h3>
                  <ConfirmButtonForm
                    action={`/draft/${playSession.slug}?index`}
                    intent="remove-team"
                    buttonChildren={'🗑️'}
                  >
                    <input type="hidden" name="teamId" value={team?.id} />
                  </ConfirmButtonForm>
                </div>
                <ul className="flex flex-col gap-2 p-2 px-3 ">
                  {team.players
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((player) => (
                      <li
                        key={player.id}
                        className="flex justify-between items-center"
                      >
                        <span>
                          <RoleEmoji rolename={player.role.name} />{' '}
                          {player.name}
                        </span>
                        {player.role.name !== 'queen' && (
                          <ConfirmButtonForm
                            action={`/draft/${playSession.slug}?index`}
                            intent="place-player"
                            buttonChildren={'❌'}
                          >
                            <input
                              type="hidden"
                              name="playSessionId"
                              value={playSession?.id}
                            />

                            <input
                              type="hidden"
                              name="playerId"
                              value={player.id}
                            />
                          </ConfirmButtonForm>
                        )}
                      </li>
                    ))}
                </ul>
              </li>
            ))}
          </ul>
          <fetcher.Form
            method="post"
            className="flex flex-col gap-3 "
            action={`/draft/${playSession.slug}?index`}
          >
            <input type="hidden" name="playSessionId" value={playSession?.id} />
            <input
              type="hidden"
              name="queens"
              value={JSON.stringify(
                playSession?.players?.filter(
                  (player) => player.role.name === 'queen'
                )
              )}
            />
            <div className="flex items-center gap-3">
              <button
                type="submit"
                name="intent"
                value="generate-teams"
                className="btn flex-grow"
              >
                {playSession?.teams?.length
                  ? playSession?.teams.length ===
                    playSession.players?.filter(
                      (player) => player.role.name === 'queen'
                    )?.length
                    ? 'Shuffle Order'
                    : 'Generate + Shuffle'
                  : 'Generate'}
              </button>
              <button
                type="submit"
                name="intent"
                value="delete-teams"
                className="btn !bg-red-700 !text-white w-fit"
              >
                Delete
              </button>
              <button
                type="submit"
                name="intent"
                value="reset-teams"
                className="btn !bg-amber-700 !text-white w-fit"
              >
                Reset
              </button>
            </div>
          </fetcher.Form>
        </div>
      </div>
    </div>
  );
}
